import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';

interface TooltipProps {
    /** The trigger element. Wrapped with Radix asChild — must be a single
     *  React element that can accept a ref (any DOM element works). */
    children: ReactNode;
    /** Tooltip contents. Can be a string or any JSX. */
    content: ReactNode;
    /** Preferred side; Radix auto-flips on collision. */
    side?: 'top' | 'right' | 'bottom' | 'left';
    align?: 'start' | 'center' | 'end';
    /** When true, renders children without the tooltip. Useful for conditional
     *  tooltips (e.g. only show hint when data is incomplete). */
    disabled?: boolean;
    /** Opt-in keyboard focusability for non-interactive triggers (span/div/p).
     *  Adds `tabIndex={0}` to the child so keyboard users can reach the
     *  tooltip; buttons/links already have native tab order so leave this off. */
    focusable?: boolean;
    /** Extra classes merged onto the content bubble. */
    className?: string;
    /** `card` swaps the yellow note for a bordered white card; the default stays `note`. */
    surface?: TooltipSurface;
}

// Animate in/out via Radix state attrs; origin follows auto-flip side.
const MOTION_CLASSES = [
    'origin-[var(--radix-tooltip-content-transform-origin)]',
    'transition-[opacity,transform] duration-150 ease-out',
    'data-[state=closed]:opacity-0 data-[state=closed]:scale-95',
    'data-[state=delayed-open]:opacity-100 data-[state=delayed-open]:scale-100',
    'data-[state=instant-open]:opacity-100 data-[state=instant-open]:scale-100'
];

const BASE_CLASSES = [
    'z-50 px-2.5 py-1.5',
    'rounded-md',
    'bg-yellow-200/95 text-gray-900',
    'shadow-[0_4px_14px_-4px_rgba(202,138,4,0.28)]',
    'text-xs font-medium tracking-tight',
    // `pre-line`, not `normal`: string content keeps the newline semantics the
    // native `title` attribute had (DataTable cell reveals join multi-part
    // values with '\n'), and content without newlines renders identically.
    'max-w-xs whitespace-pre-line break-words',
    ...MOTION_CLASSES
].join(' ');

// A white card for structured content: no padding or width, so the caller sizes and lays it out.
const CARD_CLASSES = [
    'z-50 rounded-xl border border-gray-200 bg-white text-gray-700 shadow-lg text-xs',
    ...MOTION_CLASSES
].join(' ');

type TooltipSurface = 'note' | 'card';

const SURFACES: Record<TooltipSurface, { content: string; arrow: string }> = {
    note: {
        content: BASE_CLASSES,
        arrow: 'fill-yellow-200/95 drop-shadow-[0_1px_1px_rgba(202,138,4,0.2)]'
    },
    card: { content: CARD_CLASSES, arrow: 'fill-white drop-shadow-[0_1px_0_rgb(229,231,235)]' }
};

/**
 * Mount ONCE at your app root. All `<Tooltip>` instances share this
 * provider's delay + skip-delay so hovering from one tooltip to the next
 * feels instant instead of re-triggering the open delay each time.
 */
export function TooltipProvider({
    children,
    delayDuration = 150,
    skipDelayDuration = 200
}: {
    children: ReactNode;
    delayDuration?: number;
    skipDelayDuration?: number;
}) {
    return (
        <TooltipPrimitive.Provider
            delayDuration={delayDuration}
            skipDelayDuration={skipDelayDuration}
        >
            {children}
        </TooltipPrimitive.Provider>
    );
}

export function Tooltip({
    children,
    content,
    side = 'top',
    align = 'center',
    disabled = false,
    focusable = false,
    className,
    surface = 'note'
}: TooltipProps) {
    if (disabled || content == null || content === '') return <>{children}</>;

    // When the trigger isn't natively focusable (span/div), opt-in adds
    // tabIndex so keyboard users can reach the tooltip.
    const trigger =
        focusable && isValidElement(children)
            ? cloneElement(children as ReactElement, {
                  tabIndex: (children as ReactElement).props.tabIndex ?? 0
              })
            : children;

    return (
        <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>{trigger}</TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal>
                <TooltipPrimitive.Content
                    side={side}
                    align={align}
                    sideOffset={6}
                    collisionPadding={8}
                    className={
                        className
                            ? `${SURFACES[surface].content} ${className}`
                            : SURFACES[surface].content
                    }
                >
                    {content}
                    <TooltipPrimitive.Arrow
                        className={SURFACES[surface].arrow}
                        width={10}
                        height={5}
                    />
                </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
    );
}

export default Tooltip;
